import React from "react";
import {
  styled,
  Typography,
  Box,
  Link,
  Grid2,
  Divider,
  useTheme,
} from "@mui/material";
import { useTranslation, Trans, TransProps } from "react-i18next";
import { useMandatorContext } from "@rentablo/theming";
import { useFeatureFlag } from "@hooks";
import { TermsMainTitle, TermsTitle } from "@components";

const StyledFirstBlock = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(14),
  [theme.breakpoints.down("md")]: {
    paddingTop: theme.spacing(10),
  },
}));

const StyledDivider = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(11),
  [theme.breakpoints.down("md")]: {
    paddingTop: theme.spacing(7),
  },
}));

const LinkStyled = styled(Link)(({ theme }) => ({
  width: "fit-content",
  color: theme.palette.primary.main,
}));

const TransWithNamespace = ({ i18nKey, ...rest }: TransProps<any>) => {
  const { isRentablo } = useMandatorContext();
  const namespace = isRentablo ? "privacyPage" : "privacyPageForMandators";

  return <Trans i18nKey={`${namespace}.${i18nKey}`} {...rest} />;
};

export const Privacy = () => {
  const { isRentablo } = useMandatorContext();
  const namespace = isRentablo ? "privacyPage" : "privacyPageForMandators";

  const { t, i18n } = useTranslation("translation", {
    keyPrefix: namespace,
  });
  const { t: tGlobal } = useTranslation();
  const isCashbackEnabled = useFeatureFlag("CASHBACK_WIZARD");
  const supportEmail = isRentablo ? "info@rentablo.de" : "info@wealthapi.eu";

  const theme = useTheme();
  return (
    <Grid2 container>
      <Grid2
        size={{
          xs: 12,
          lg: 8,
        }}
      >
        <TermsMainTitle variant="h1">{t("title")}</TermsMainTitle>
        <Typography variant="subtitle1" mt={3}>
          {t("subtitle")}
        </Typography>

        <Box
          sx={{ backgroundColor: theme.palette.background.selected }}
          borderRadius="4px"
          padding="8px 12px"
          width="fit-content"
          mb={6}
          mt={theme.breakpoints.down("md") ? 6 : 11}
        >
          <Typography color={theme.palette.primary.dark} fontWeight="600">
            {t("card.1")}
          </Typography>
          <Typography color={theme.palette.primary.dark}>
            {t("card.2")}
          </Typography>
        </Box>

        <StyledFirstBlock whiteSpace="pre-line">
          <TermsTitle variant="h4" mb={6}>
            {t("1.title")}
          </TermsTitle>
          <Typography
            component="div"
            variant="body3"
            color="textSecondary"
            whiteSpace="pre-line"
            mt={3}
          >
            <TransWithNamespace i18nKey="1.text">
              {[
                <LinkStyled key="phone" href="tel:(0) 30 - 71 53 30 09}" />,
                <LinkStyled
                  key="email"
                  href="mailto:datenschutz@rentablo.de"
                />,
                <Typography
                  variant="body3"
                  color="textPrimary"
                  key="caption"
                  component="span"
                />,
              ]}
            </TransWithNamespace>
          </Typography>
        </StyledFirstBlock>
        <StyledDivider>
          <Divider />
        </StyledDivider>
      </Grid2>
      <Grid2
        size={{
          xs: 12,
          lg: 8,
        }}
      >
        <Box whiteSpace="pre-line">
          <TermsTitle variant="h4" mb={6}>
            {t("2.title")}
          </TermsTitle>
          <Typography
            component="div"
            variant="body3"
            color="textSecondary"
            whiteSpace="pre-line"
            mt={3}
          >
            <TransWithNamespace i18nKey="2.text">
              {[
                <Typography key="a" variant="h6" mb={3} color="textPrimary" />,
                <Typography key="b" variant="h6" mb={3} color="textPrimary" />,
                <Typography key="c" variant="h6" mb={3} color="textPrimary" />,
              ]}
            </TransWithNamespace>
          </Typography>
        </Box>
        <StyledDivider>
          <Divider />
        </StyledDivider>
      </Grid2>
      <Grid2
        size={{
          xs: 12,
          lg: 8,
        }}
      >
        <Box whiteSpace="pre-line">
          <TermsTitle variant="h4" mb={6}>
            {t("3.title")}
          </TermsTitle>
          <Typography
            component="div"
            variant="body3"
            color="textSecondary"
            whiteSpace="pre-line"
            mt={3}
          >
            <TransWithNamespace i18nKey="3.text">
              {[
                <Typography variant="body3" color="textPrimary" key="a" />,
                <Typography variant="body3" color="textPrimary" key="b" />,
                <Typography variant="body3" color="textPrimary" key="c" />,
                <Typography variant="body3" color="textPrimary" key="d" />,

                ...(isCashbackEnabled
                  ? [
                      <Typography
                        variant="body3"
                        color="textPrimary"
                        key="e"
                      />,
                      <Typography variant="body3" key="f" />,
                    ]
                  : [<Box display="none" />, <Box display="none" />]),
              ]}
            </TransWithNamespace>
          </Typography>
        </Box>
        <StyledDivider>
          <Divider />
        </StyledDivider>
      </Grid2>
      <Grid2
        size={{
          xs: 12,
          lg: 8,
        }}
      >
        <Box whiteSpace="pre-line">
          <TermsTitle variant="h4" mb={6}>
            {t("4.title")}
          </TermsTitle>
          <Typography
            component="div"
            variant="body3"
            color="textSecondary"
            whiteSpace="pre-line"
            mt={3}
          >
            <TransWithNamespace i18nKey="4.text">
              {[
                <LinkStyled
                  key="0"
                  href="https://www.google.com"
                  target="_blank"
                  rel="noreferrer"
                />,
                <LinkStyled
                  key="1"
                  href="https://www.google.com/intl/de/policies/privacy/"
                  target="_blank"
                  rel="noreferrer"
                />,
                <LinkStyled
                  key="2"
                  href="https://tools.google.com/dlpage/gaoptout?hl=de"
                  target="_blank"
                  rel="noreferrer"
                />,
                <LinkStyled
                  key="3"
                  href="https://de-de.facebook.com/policy.php"
                  target="_blank"
                  rel="noreferrer"
                />,
                <LinkStyled
                  key="4"
                  href="https://de-de.facebook.com/privacy/explanation.php"
                  target="_blank"
                  rel="noreferrer"
                />,
              ]}
            </TransWithNamespace>
          </Typography>
        </Box>
        <StyledDivider>
          <Divider />
        </StyledDivider>
      </Grid2>
      <Grid2
        size={{
          xs: 12,
          lg: 8,
        }}
      >
        <Box whiteSpace="pre-line">
          <TermsTitle variant="h4" mb={6}>
            {t("5.title")}
          </TermsTitle>
          <Typography
            component="div"
            variant="body3"
            color="textSecondary"
            whiteSpace="pre-line"
            mt={3}
          >
            <TransWithNamespace i18nKey="5.text">
              {[
                <LinkStyled
                  key="0"
                  href="https://www.hotjar.com"
                  target="_blank"
                  rel="noreferrer"
                />,
                <Typography
                  key="1"
                  component="div"
                  variant="h6"
                  color="textPrimary"
                  mt={6}
                  mb={3}
                />,
                <LinkStyled
                  key="2"
                  href="https://www.hotjar.com/opt-out"
                  target="_blank"
                  rel="noreferrer"
                />,
                <LinkStyled
                  key="3"
                  href="https://www.hotjar.com/privacy"
                  target="_blank"
                  rel="noreferrer"
                />,
                <Typography
                  key="4"
                  component="div"
                  variant="h6"
                  color="textPrimary"
                  mt={6}
                  mb={3}
                />,
                <Typography
                  key="5"
                  component="div"
                  variant="h6"
                  color="textPrimary"
                  mt={6}
                  mb={3}
                />,
                <Typography
                  key="6"
                  component="div"
                  variant="h6"
                  color="textPrimary"
                  mt={6}
                  mb={3}
                />,
                isCashbackEnabled ? (
                  <Typography variant="body3" component="span" key="f" />
                ) : (
                  <Box display="none" />
                ),
              ]}
            </TransWithNamespace>
          </Typography>
        </Box>
        <StyledDivider>
          <Divider />
        </StyledDivider>
      </Grid2>
      <Grid2
        size={{
          xs: 12,
          lg: 8,
        }}
      >
        <Box whiteSpace="pre-line">
          <TermsTitle variant="h4" mb={6}>
            {t("6.title")}
          </TermsTitle>
          <Typography
            component="div"
            variant="body3"
            color="textSecondary"
            whiteSpace="pre-line"
            mt={3}
          >
            {t("6.text")}
          </Typography>
        </Box>
        <StyledDivider>
          <Divider />
        </StyledDivider>
      </Grid2>
      <Grid2
        size={{
          xs: 12,
          lg: 8,
        }}
      >
        <Box whiteSpace="pre-line">
          <TermsTitle variant="h4" mb={6}>
            {t("7.title")}
          </TermsTitle>
          <Typography
            component="div"
            variant="body3"
            color="textSecondary"
            whiteSpace="pre-line"
            mt={3}
          >
            {t("7.text")}
          </Typography>
        </Box>
        <StyledDivider>
          <Divider />
        </StyledDivider>
      </Grid2>
      <Grid2
        size={{
          xs: 12,
          lg: 8,
        }}
      >
        <Box whiteSpace="pre-line">
          <TermsTitle variant="h4" mb={6} id="privacy-contact">
            {t("8.title")}
          </TermsTitle>
          <Typography
            component="div"
            variant="body3"
            color="textSecondary"
            whiteSpace="pre-line"
            mt={3}
          >
            <TransWithNamespace i18nKey="8.text">
              {[
                <Typography
                  key="0"
                  component="span"
                  color="textPrimary"
                  variant="body3"
                />,
                <LinkStyled key="email" href={`mailto:${supportEmail}`} />,
              ]}
            </TransWithNamespace>
          </Typography>
        </Box>
        {i18n.language === "en" && (
          <Typography variant="body3" color="textSecondary" mt={10}>
            {tGlobal("common.englishLegalNote")}
          </Typography>
        )}
      </Grid2>
    </Grid2>
  );
};
